import { addressObjectSchema, type AddressObject } from "@mailflare/shared";

import { ApiError, apiRequest } from "@/lib/api-client";

export interface ContactSuggestion {
  readonly name?: string | undefined;
  readonly address: string;
}

/**
 * Best-effort address autocomplete. The contacts endpoint is optional in the
 * API surface, so a 404 degrades to an empty suggestion list rather than
 * surfacing an error.
 */
export async function searchContacts(
  query: string,
  signal?: AbortSignal,
): Promise<readonly ContactSuggestion[]> {
  try {
    const raw = await apiRequest<unknown>("/contacts", {
      query: { q: query },
      ...(signal !== undefined ? { signal } : {}),
    });
    return addressObjectSchema.array().parse(raw);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return [];
    throw error;
  }
}

/** Converts a user-entered string into an address object when valid. */
export function parseRecipient(input: string): AddressObject | null {
  const value = input.trim();
  const angle = /^(.*?)<([^>]+)>$/.exec(value);
  const rawAddress = angle ? (angle[2] ?? "").trim() : value;
  const candidate = addressObjectSchema.safeParse(
    angle !== null && (angle[1] ?? "").trim().length > 0
      ? { name: (angle[1] ?? "").trim(), address: rawAddress }
      : { address: rawAddress },
  );
  if (candidate.success) return candidate.data;
  return null;
}
